// BrimLight/client/helpers/video-editor/AIModifyModal.js
// --- REPLACE THE ENTIRE FILE ---

import { ComponentBase } from "../../core/ComponentBase.js";

export class AIModifyModal extends ComponentBase {
    static styles = `
        .ai-modify-modal .modal-dialog {
            max-width: 600px;
        }
        .ai-modify-modal .modal-body {
            min-height: 400px;
        }
        .additional-options-container {
            padding: 1rem;
            background-color: var(--bs-tertiary-bg);
            border-radius: var(--bs-border-radius);
            margin-top: 1rem;
        }
        /* --- NEW STYLES for Time Range --- */
        .time-range-controls {
            border: 1px solid var(--bs-border-color-translucent);
            border-radius: var(--bs-border-radius);
            padding: 1rem;
        }
        .time-range-slider-group {
            display: flex;
            align-items: center;
            gap: 1rem;
        }
        .time-range-slider-group input[type=range] {
            flex-grow: 1;
        }
        .time-range-slider-group input[type=number] {
            width: 80px;
        }
    `;

    constructor(config) {
        super(config);
        this.totalDuration = 0;
        this.currentTime = 0;
        this.videoStyles = [
            'cinematic_realistic', 'pixar_animation', 'ghibli_style', 'anime_90s', 'claymation', 
            'stop_motion', 'watercolor_painting', 'oil_painting', 'charcoal_sketch', 'pencil_drawing', 
            'comic_book', 'graphic_novel', 'low_poly_3d', 'vaporwave', 'cyberpunk', 'steampunk', 
            'fantasy_art', 'sci_fi_concept_art', 'vintage_film', 'noir_film', 'documentary', 'isometric'
        ];
        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'modal fade ai-modify-modal';
        this.container.id = `ai-modify-modal-${this.config.instanceId}`;
        this.container.setAttribute('tabindex', '-1');
        this.container.innerHTML = this.renderModalStructure();
        document.body.appendChild(this.container);
        this.modalInstance = new bootstrap.Modal(this.container);

        this.form = this.container.querySelector('form');
        this.typeSelect = this.container.querySelector('#ai-modification-type');
        this.optionsContainer = this.container.querySelector('#additional-options-container');
        
        // Time Range Elements
        this.startSlider = this.container.querySelector('#time-range-start-slider');
        this.endSlider = this.container.querySelector('#time-range-end-slider');
        this.startInput = this.container.querySelector('#time-range-start-input');
        this.endInput = this.container.querySelector('#time-range-end-input');

        this.form.addEventListener('submit', this.handleSubmit.bind(this));
        this.typeSelect.addEventListener('change', this.renderAdditionalOptions.bind(this));
        
        this.setupTimeRangeListeners();
        
        await super.initialize();
    }

    show(totalDuration, currentTime) {
        this.totalDuration = totalDuration;
        this.currentTime = currentTime;
        
        this.form.reset();
        
        // Configure sliders and inputs
        [this.startSlider, this.endSlider, this.startInput, this.endInput].forEach(el => {
            el.max = totalDuration.toFixed(1);
            el.step = 0.1;
        });
        
        this.startSlider.value = 0;
        this.startInput.value = '0.0';
        this.endSlider.value = totalDuration.toFixed(1);
        this.endInput.value = totalDuration.toFixed(1);

        this.renderAdditionalOptions();
        this.modalInstance.show();
    }

    hide() {
        this.modalInstance.hide();
    }

    setupTimeRangeListeners() {
        // Preset buttons
        this.container.querySelector('#preset-whole-timeline').addEventListener('click', () => {
            this.startSlider.value = 0;
            this.endSlider.value = this.totalDuration;
            this.syncInputs('slider');
        });
        this.container.querySelector('#preset-from-playhead').addEventListener('click', () => {
            this.startSlider.value = this.currentTime;
            this.endSlider.value = this.totalDuration;
            this.syncInputs('slider');
        });

        // Slider listeners
        this.startSlider.addEventListener('input', () => this.syncInputs('slider', 'start'));
        this.endSlider.addEventListener('input', () => this.syncInputs('slider', 'end'));

        // Number input listeners
        this.startInput.addEventListener('input', () => this.syncInputs('input', 'start'));
        this.endInput.addEventListener('input', () => this.syncInputs('input', 'end'));
    }

    syncInputs(source, changed = null) {
        let startVal = parseFloat(this.startSlider.value);
        let endVal = parseFloat(this.endSlider.value);

        if (source === 'input') {
            startVal = parseFloat(this.startInput.value);
            endVal = parseFloat(this.endInput.value);
        }

        // Enforce constraints: start <= end
        if (changed === 'start' && startVal > endVal) {
            endVal = startVal;
        }
        if (changed === 'end' && endVal < startVal) {
            startVal = endVal;
        }

        // Update all elements to be in sync
        this.startSlider.value = startVal.toFixed(1);
        this.startInput.value = startVal.toFixed(1);
        this.endSlider.value = endVal.toFixed(1);
        this.endInput.value = endVal.toFixed(1);
    }

    handleSubmit(event) {
        event.preventDefault();
        const formData = new FormData(this.form);
        const data = {
            modificationType: formData.get('modificationType'),
            userRequest: formData.get('userRequest'),
            timeRange: {
                start: parseFloat(formData.get('timeRangeStart')),
                end: parseFloat(formData.get('timeRangeEnd'))
            },
            options: {}
        };

        switch (data.modificationType) {
            case 'ChangeVideoStyle':
                data.options.newStyle = formData.get('newVideoStyle');
                break;
            case 'EnhanceWithAudio':
                data.options.musicMood = formData.get('musicMood');
                break;
            case 'ChangeVideoDuration':
                data.options.newDuration = parseInt(formData.get('newDuration'), 10);
                break;
            case 'GenerateSubtitles':
                data.options.fontStyle = formData.get('fontStyle');
                data.options.position = formData.get('subtitlePosition');
                break;
        }

        this.emit('submit', data);
        this.hide();
    }

    renderModalStructure() {
        return `
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <form>
                        <div class="modal-header">
                            <h5 class="modal-title"><i class="bi bi-magic me-2"></i>AI Editor</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div class="modal-body">
                            <div class="mb-3">
                                <label for="ai-modification-type" class="form-label">Modification Type</label>
                                <select id="ai-modification-type" name="modificationType" class="form-select">
                                    <option value="ReorganizeTimeline">Reorganize Timeline</option>
                                    <option value="ModifyStoryNarrative">Modify Story Narrative</option>
                                    <option value="ChangeVideoStyle">Change Video Style</option>
                                    <option value="EnhanceWithAudio">Enhance with Audio</option>
                                    <option value="ChangeVideoDuration">Change Video Duration</option>
                                    <option value="GenerateSubtitles">Generate Subtitles</option>
                                </select>
                            </div>
                            <div class="mb-3">
                                <label for="ai-user-request" class="form-label">Your Request</label>
                                <textarea id="ai-user-request" name="userRequest" class="form-control" rows="3" placeholder="e.g., 'Make the ending happier by having the characters reconcile.' or 'Add suspenseful music to this section.'"></textarea>
                            </div>
                            
                            <div class="time-range-controls">
                                <div class="d-flex justify-content-between align-items-center mb-2">
                                    <label class="form-label mb-0">Time Range</label>
                                    <div class="btn-group btn-group-sm">
                                        <button type="button" class="btn btn-outline-secondary" id="preset-whole-timeline">Whole Timeline</button>
                                        <button type="button" class="btn btn-outline-secondary" id="preset-from-playhead">From Playhead</button>
                                    </div>
                                </div>
                                <div class="time-range-slider-group mb-2">
                                    <label for="time-range-start-slider" class="form-label small mb-0">Start</label>
                                    <input type="range" id="time-range-start-slider" class="form-range">
                                    <input type="number" id="time-range-start-input" name="timeRangeStart" class="form-control form-control-sm">
                                </div>
                                <div class="time-range-slider-group">
                                    <label for="time-range-end-slider" class="form-label small mb-0">End</label>
                                    <input type="range" id="time-range-end-slider" class="form-range">
                                    <input type="number" id="time-range-end-input" name="timeRangeEnd" class="form-control form-control-sm">
                                </div>
                            </div>

                            <div id="additional-options-container"></div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                            <button type="submit" class="btn btn-primary">Start AI Edit</button>
                        </div>
                    </form>
                </div>
            </div>
        `;
    }

    renderAdditionalOptions() {
        const type = this.typeSelect.value;
        let html = '<h6>Additional Options</h6>';
        switch (type) {
            case 'ChangeVideoStyle':
                const styleOptions = this.videoStyles.map(style => 
                    `<option value="${style}">${style.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>`
                ).join('');
                html += `<div class="mb-2"><label for="new-video-style" class="form-label">New Video Style</label><select id="new-video-style" name="newVideoStyle" class="form-select">${styleOptions}</select></div>`;
                break;
            case 'EnhanceWithAudio':
                html += `<div class="mb-2"><label for="music-mood" class="form-label">Music Mood</label><select id="music-mood" name="musicMood" class="form-select"><option value="uplifting">Uplifting</option><option value="dramatic">Dramatic</option><option value="suspenseful">Suspenseful</option><option value="calm">Calm</option><option value="energetic">Energetic</option></select></div>`;
                break;
            case 'ChangeVideoDuration':
                html += `<div class="mb-2"><label for="new-duration" class="form-label">New Target Duration (seconds)</label><input type="number" id="new-duration" name="newDuration" class="form-control" value="${Math.round(this.totalDuration)}" step="1"></div>`;
                break;
            case 'GenerateSubtitles':
                html += `<div class="mb-2"><label for="font-style" class="form-label">Font Style</label><select id="font-style" name="fontStyle" class="form-select"><option value="default">Default</option><option value="cinematic">Cinematic</option><option value="playful">Playful</option></select></div><div class="mb-2"><label for="subtitle-position" class="form-label">Position</label><select id="subtitle-position" name="subtitlePosition" class="form-select"><option value="bottom-center">Bottom Center</option><option value="bottom-left">Bottom Left</option><option value="top-center">Top Center</option></select></div>`;
                break;
            default:
                html += `<p class="text-muted small">No additional options for this modification type.</p>`;
                break;
        }
        this.optionsContainer.innerHTML = html;
    }

    render() { return this.container; }

    destroy() {
        this.modalInstance?.dispose();
        this.container?.remove();
        super.destroy();
    }
}